<template>
    <div>
        <g-loader v-if="creatorStore.isLoading" />
        <div v-else>
            <div style="margin-bottom: 0.5rem">
                <g-typography
                    v-if="errorMessage"
                    tag="div"
                    color="error"
                    :value="errorMessage"
                    class="margin-bottom-normal"
                />
            </div>
            <div v-if="showSharedNetworks">
                <shared-networks
                    :network-id="sharedNetworks.networkId"
                    :social-network-id="sharedNetworks.socialNetworkId"
                    @back="handleCloseSharedNetworks"
                    @proceed-to-connect="proceedToConnect"
                    @popup-closed="handlePopupClosed"
                    @success="handleShareNetworkSuccess"
                />
            </div>
            <div v-for="network in networks" :key="network.id" class="margin-bottom-normal">
                <contact-network-item
                    v-if="isConnected(network) && !showSharedNetworks"
                    :contact-network="getConnectedContactNetwork(network)"
                    :context="context"
                    :required="isRequired(network)"
                    @load-contact="$emit('load-contact')"
                    @connect-instagram="showInstagramConnection"
                    @disconnect-network="disconnectNetwork"
                />
                <float-on-mask v-else tag="div" :float-up="readonly">
                    <connect-network-item
                        v-if="!showSharedNetworks"
                        :network="network"
                        :contact-network="getContactNetwork(network)"
                        :contact-id="contact.id"
                        :context="context"
                        :required="isRequired(network)"
                        :proceed-to-connect="proceedToConnectProp"
                        @load-contact="reloadContact"
                        @show-shared-networks="handleShowSharedNetworks"
                        @popup-closed="handlePopupClosed"
                        @network-connection-info="handleNetworkConnect"
                        @clear-proceed-to-connect="clearProceedToConnect"
                        @meta-connection-drawer-closed="handleMetaConnectionDrawerClosed"
                    />
                </float-on-mask>
            </div>
        </div>
    </div>
</template>

<script>
    import FloatOnMask from '../../../../library/FloatOnMask.vue';
    import ConnectNetworkItem from '../../../networks/partials/ConnectNetworkItem.vue';
    import ContactNetworkItem from '../../../networks/partials/ContactNetworkItem.vue';
    import { INSTAGRAM_ID } from '../../../../../constants/networks';
    import useNetworks from '../../../../../../src/composables/use-networks';
    import SharedNetworks from '../../../../../../app-v2/components/data-views/sharedNetworks/SharedNetworks.vue';
    import { useCreatorStore } from '../../../../../../stores';

    export default {
        name: 'ActivationBriefNetworkManager',
        components: {
            FloatOnMask,
            ConnectNetworkItem,
            ContactNetworkItem,
            SharedNetworks
        },
        props: {
            brief: {
                type: Object,
                required: true
            },
            contact: {
                type: Object,
                required: true
            },
            context: {
                type: String,
                required: true
            },
            networks: {
                type: Array,
                default: () => []
            },
            contactNetworks: {
                type: Array,
                default: () => []
            },
            requiredNetworks: {
                type: Array,
                default: () => []
            },
            tasks: {
                type: Array,
                default: () => []
            },
            readonly: {
                type: Boolean,
                default: false
            }
        },
        data() {
            const creatorStore = useCreatorStore();

            return {
                errorMessage: null,
                showSharedNetworks: false,
                sharedNetworks: {
                    networkId: null,
                    socialNetworkId: null
                },
                creatorStore,
                proceedToConnectProp: {
                    value: false,
                    networkId: 0
                }
            };
        },
        computed: {
            instagramAccount() {
                return this.contactNetworks.find((contactNetwork) => contactNetwork.network_id === INSTAGRAM_ID);
            }
        },
        methods: {
            isConnected(network) {
                return !!this.getConnectedContactNetwork(network);
            },

            isRequired(network) {
                return !!this.requiredNetworks
                    .filter((n) => n.slug !== 'facebook')
                    .find((n) => {
                        return n.slug === network.slug;
                    });
            },

            getConnectedContactNetwork(network) {
                return useNetworks().getConnectedContactNetworkByIdFromContactNetworks(
                    network?.id,
                    this.contactNetworks
                );
            },

            getContactNetwork(network) {
                return useNetworks().getContactNetworkByIdFromContactNetworks(network?.id, this.contactNetworks);
            },

            async reloadContact() {
                await this.$root.reloadContact('networks');
                this.$emit('load-contact');
                this.$emit('show-shared-networks', false);
            },

            async reloadSharedNetworks() {
                await this.creatorStore.getAllLiveUrls();
                await this.creatorStore.getAllSocialNetworks();
            },
            handleCloseSharedNetworks() {
                this.$emit('show-shared-networks', false);
                this.showSharedNetworks = false;
            },

            handleShowSharedNetworks(networkId, socialNetworkId) {
                this.sharedNetworks.networkId = networkId;
                this.sharedNetworks.socialNetworkId = socialNetworkId;
                this.$emit('show-shared-networks', true);
                this.showSharedNetworks = true;
            },

            handleShareNetworkSuccess() {
                this.handleCloseSharedNetworks();
                this.reloadContact();
                this.reloadSharedNetworks();
            },
            handleNetworkConnect(networkId) {
                this.displayNetworkId = networkId;
                this.sharedNetworks.networkId = networkId;
                this.$emit('start-account-connection');
            },

            async handlePopupClosed(networkId) {
                await this.reloadContact();
                await this.reloadSharedNetworks();

                const socialNetwork = this.creatorStore.getCurrentLiveUrlSocialNetwork(networkId);
                if (useNetworks().showSharedNetworkPostConnect(socialNetwork, this.$root.networks)) {
                    this.sharedNetworks.socialNetworkId = socialNetwork.id;
                    this.sharedNetworks.networkId = networkId;
                    this.$emit('show-shared-networks', true);
                    this.showSharedNetworks = true;
                }
            },

            handleMetaConnectionDrawerClosed() {
                this.$emit('meta-connection-drawer-closed');
            },

            /**
             * Redirects to connect to Instagram
             */
            showInstagramConnection() {
                const username = this.instagramAccount ? this.instagramAccount.network_username : '';

                if (this.context === 'brief') {
                    return this.$router.push({
                        name: 'v2.activation.brief.networks.network',
                        query: {
                            stories: true,
                            username,
                            networkId: INSTAGRAM_ID
                        }
                    });
                }
            },

            /**
             * Disconnect the given network
             *
             * @param {object} network
             */
            disconnectNetwork(network) {
                const slug = network.network.slug;
                this.$root.loaderStart(`network.${slug}.disconnect`);
                this.$http.get(`/contact/${this.contact.id}/networks/${network.id}/remove`).then(
                    () => {
                        this.$root.loaderStop(`network.${slug}.disconnect`);
                        this.reloadContact();
                        this.reloadSharedNetworks();
                    },
                    () => {
                        this.$root.loaderStop(`network.${slug}.disconnect`);
                        this.errorMessage = 'Could not remove network, please try again or contact support.';
                    }
                );
            },
            proceedToConnect(networkId) {
                this.showSharedNetworks = false;
                this.proceedToConnectProp = {
                    value: true,
                    networkId: networkId
                }
            },
            clearProceedToConnect() {
                this.proceedToConnectProp = {
                    value: false,
                    networkId: 0
                }
            },
        },

        mounted() {
            const connectedNetworkId = this.$route?.query?.connectedNetworkId;
            if (connectedNetworkId) {
                const query = Object.assign({}, this.$route?.query);
                delete query.connectedNetworkId;
                this.$router?.replace({ query });
                this.handlePopupClosed(+connectedNetworkId);
            } else {
                this.reloadSharedNetworks();
            }
        }
    };
</script>

<style lang="scss" scoped>
    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }
</style>
